import { useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import {
  BaseComponentWithContextProps,
  componentWithContext,
} from '../../../../base/customHooks/componentHOC'
import Table from '../../../../components/Table'
import IPButton from '../../../../components/IPButton'
import IPInput from '../../../../components/IPInput'
import { useSearchParams } from 'react-router-dom'
import { useCustomReducer } from '../../../../base/customHooks'
import Modal from '../../../../components/IPModal/IPModal'

import {
  IOrderDetail,
  PreOrderListModel,
  IGetMyOrdersResponse,
  IGetOrderDetailsResponse,
} from '../models/models'
import { GetPreOrderApi } from '../services/getPreOrderApi'
import { CancelOrderApi } from '../services/cancelOrderApi'

// ✅ Yeni servisler: Bayi Onay Bekleyenleri Onayla / Reddet (ID List ile)
import { SendDealerApprovalApi } from '../services/sendDealerApprovalApi'
import { SendDealerRejectApi } from '../services/sendDealerRejectApi'

interface PreOrderState {
  orderDetails: IOrderDetail
  showOrderDetails: boolean
  errorMessage: []
}

function EditPreOrder(props: BaseComponentWithContextProps) {
  const [searchParams] = useSearchParams()

  const [list, setList] = useState<PreOrderListModel[]>([])
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [headerInfo, setHeaderInfo] = useState<Partial<PreOrderListModel>>({})

  // Sipariş No Detay Modal
  const [showOrderNoModal, setShowOrderNoModal] = useState<boolean>(false)
  const [selectedOrderNo, setSelectedOrderNo] = useState<string>('')
  const [selectedOrderRows, setSelectedOrderRows] = useState<PreOrderListModel[]>([])

  const getInitialState = (): PreOrderState => ({
    orderDetails: {
      DealerName: '',
      Address: '',
      City: '',
      District: '',
      OrderNumber: '',
      Currency: 0,
      CreatedDate: new Date(),
      ShipmentDate: new Date(),
      ShipmentType: '',
      OrderReturnCompanyList: [
        {
          Cargo: '',
          CargoValue: 0,
          Items: [
            {
              Warehouse: '',
              ProductCode: '',
              ProductName: '',
              Quantity: 0,
              Currency: 0,
              Price1: '',
              Price1Value: 0,
              Price2: '',
              Price2Value: 0,
              TotalPrice1: '',
              TotalPrice1Value: 0,
              TotalPrice2: '',
              TotalPrice2Value: 0,
            },
          ],
          Company: '',
          OrderNumber: '',
          PaymentMethod: '',
          CurrencyType: '',
          CurrencyValue: '',
          MT: '',
          TotalPrice: '',
          TotalKdv: '',
          Total: '',
        },
      ],
      Total: 0,
      ErrorMessages: [],
      IsStockError: [],
    },
    showOrderDetails: false,
    errorMessage: [],
  })

  const [state, dispatchState] = useCustomReducer<PreOrderState>(getInitialState())

  // ===== Helpers =====
  const hasOrderNo = (row: PreOrderListModel) =>
    (row.SaporderNumber?.trim()?.length ?? 0) > 0

  const isCancelledStatus = (row: PreOrderListModel) => row.Status === -1

  // DealerApproval:
  // 0: Yönetici Onayı Bekliyor
  // 1: Onay Bekliyor
  // 2: Onaylandı
  // -1: Reddedildi
  const getDealerApproval = (row: PreOrderListModel): number | undefined => {
    const v = (row as any)?.DealerApproval
    return typeof v === 'number' ? v : undefined
  }

  const isDealerApprovalPendingRow = (row: PreOrderListModel) => getDealerApproval(row) === 1

  const getDealerApprovalLabel = (row: PreOrderListModel): string => {
    const a = getDealerApproval(row)
    if (a === 0) return 'Yönetici Onayı Bekliyor'
    if (a === 1) return 'Onayınız Bekliyor'
    if (a === 2) return 'Onaylandı'
    if (a === -1) return 'Reddedildi'
    return 'Bilinmiyor'
  }

  const stripTimeFromSapDate = (s?: string) => {
    if (!s) return ''
    return s.split(' ')[0] || s
  }

  const currencySymbolFromCode = (code?: string) => {
    const c = (code || '').toUpperCase().trim()
    if (c === 'TRY' || c === 'TL') return '₺'
    if (c === 'USD') return '$'
    if (c === 'EUR') return '€'
    return c || ''
  }

  const currencySymbolFromRow = (row?: PreOrderListModel) => {
    const raw = (row?.Currency || '').trim()
    if (raw === '₺' || raw === '$' || raw === '€') return raw
    return currencySymbolFromCode(raw)
  }

  const getStatusLabel = (status?: number): string => {
    switch (status) {
      case 1:
        return 'Oluşturuldu'
      case 2:
        return 'Sevk edildi'
      case -1:
        return 'İptal edildi'
      case 0:
      default:
        return 'Bekliyor'
    }
  }

  const formatMoney2 = (value: any) => {
    const n = Number(value)
    if (Number.isNaN(n)) return null
    return n.toFixed(2)
  }

  // Sipariş No Modal aç
  const openOrderNoModal = (orderNo: string) => {
    const clean = (orderNo || '').trim()
    if (!clean) return
    const rows = list.filter(x => (x.SaporderNumber || '').trim() === clean)
    setSelectedOrderNo(clean)
    setSelectedOrderRows(rows)
    setShowOrderNoModal(true)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))
  }

  const reloadByRef = async () => {
    const referenceNumber = searchParams.get('refNumber')
    if (!referenceNumber) return

    props.showLoading()
    try {
      const data = await new GetPreOrderApi({}).getPreOrder(referenceNumber)
      props.hideLoading()
      setList(data)
      if (data.length > 0) setHeaderInfo(data[0])
      setSelectedIds([])
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description || 'Ön sipariş verileri yüklenirken bir hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // ===== Load =====
  useEffect(() => {
    const referenceNumber = searchParams.get('refNumber')

    if (!referenceNumber) {
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: 'Referans numarası bulunamadı',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    reloadByRef()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  // ===== Cancel order (seçili ürünler) =====
  // ✅ İPTAL KURALI:
  // - İptal sadece sipariş numarası OLUŞMAMIŞ ürünler için yapılmalı
  // - Sipariş numarası olan ürünler iptal edilemez
  // - İptal edilmiş ürünler tekrar iptal edilemez
  const handleCancelSelected = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const invalidItems = selectedRows.filter(x => hasOrderNo(x))
    const cancelledItems = selectedRows.filter(x => isCancelledStatus(x))

    if (invalidItems.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilemeyen ürün(ler)',
        text: 'Sipariş numarası oluşmuş ürünler için iptal işlemi yapılamaz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (cancelledItems.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'İptal edilemeyen ürün(ler)',
        text: 'İptal edilmiş ürün(ler) tekrar iptal edilemez.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili ürünler iptal edilsin mi?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    props.showLoading()
    const api = new CancelOrderApi({})
    api
      .cancelOrder(selectedIds)
      .then((res: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'success',
          title: res,
          timer: 1500,
          showConfirmButton: false,
        }).then(() => reloadByRef())
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err?.description || 'Sipariş iptal edilirken bir hata oluştu',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  // ===== ✅ Bayi Onay Bekleyenleri Onayla (DealerApproval === 1) =====
  // ✅ BUTONLAR DISABLE OLMAYACAK:
  // - Seçim yoksa uyarı
  // - Seçim var ama pending yoksa uyarı
  const handleApproveSelected = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const pendingRows = selectedRows.filter(isDealerApprovalPendingRow)
    const nonPendingRows = selectedRows.filter(x => !isDealerApprovalPendingRow(x))

    if (pendingRows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Onaylanacak ürün bulunamadı',
        text: 'Seçtiğiniz ürünler içinde Onay Bekliyor durumunda ürün yok.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (nonPendingRows.length > 0) {
      const firstNames = nonPendingRows
        .slice(0, 5)
        .map(x => x.ProductName)
        .join(', ')
      const extra = nonPendingRows.length > 5 ? ` (+${nonPendingRows.length - 5} ürün)` : ''

      await Swal.fire({
        icon: 'warning',
        title: 'Seçili ürünlerin bir kısmı onay beklemiyor',
        html: `<div>Bu ürünler işleme dahil edilmeyecek.<br/>Sadece <b>Onay Bekliyor</b> ürünler onaylanır.<br/><br/><b>Örnek:</b> ${firstNames}${extra}</div>`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili Onay Bekleyen ürünler onaylansın mı?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    const idList = pendingRows.map(x => x.Id)

    props.showLoading()
    const api = new SendDealerApprovalApi({})

    try {
      const res: any = await api.approveDealerApproval(idList)
      props.hideLoading()
      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: res || 'Seçili ürünler onaylandı',
        showConfirmButton: true,
        allowOutsideClick: false,
      }).then(() => reloadByRef())
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description || err?.message || 'Onaylama sırasında hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // ===== ✅ Bayi Onay Bekleyenleri Reddet (DealerApproval === 1) =====
  const handleRejectSelected = async () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Lütfen en az bir ürün seçiniz.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    const selectedRows = list.filter(x => selectedIds.includes(x.Id))
    const pendingRows = selectedRows.filter(isDealerApprovalPendingRow)
    const nonPendingRows = selectedRows.filter(x => !isDealerApprovalPendingRow(x))

    if (pendingRows.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Reddedilecek ürün bulunamadı',
        text: 'Seçtiğiniz ürünler içinde Onay Bekliyor durumunda ürün yok.',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
      return
    }

    if (nonPendingRows.length > 0) {
      const firstNames = nonPendingRows
        .slice(0, 5)
        .map(x => x.ProductName)
        .join(', ')
      const extra = nonPendingRows.length > 5 ? ` (+${nonPendingRows.length - 5} ürün)` : ''

      await Swal.fire({
        icon: 'warning',
        title: 'Seçili ürünlerin bir kısmı onay beklemiyor',
        html: `<div>Bu ürünler işleme dahil edilmeyecek.<br/>Sadece <b>Onay Bekliyor</b> ürünler reddedilir.<br/><br/><b>Örnek:</b> ${firstNames}${extra}</div>`,
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }

    const confirmResult = await Swal.fire({
      icon: 'question',
      title: 'Seçili Onay Bekleyen ürünler reddedilsin mi?',
      showConfirmButton: true,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
      allowOutsideClick: false,
    })
    if (!confirmResult.isConfirmed) return

    const idList = pendingRows.map(x => x.Id)

    props.showLoading()
    const api = new SendDealerRejectApi({})

    try {
      const res: any = await api.rejectDealerApproval(idList)
      props.hideLoading()
      Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: res || 'Seçili ürünler reddedildi',
        showConfirmButton: true,
        allowOutsideClick: false,
      }).then(() => reloadByRef())
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err?.description || err?.message || 'Reddetme sırasında hata oluştu',
        showConfirmButton: true,
        allowOutsideClick: false,
      })
    }
  }

  // ===== Table =====
  const tableColumns = useMemo(
    () => [
      { Header: '#', accessor: 'rowNo' },
      { Header: 'Ürün (Excel)', accessor: 'productName' },
      { Header: 'Ürün (Sistem)', accessor: 'productNameFromDb' },
      { Header: 'Ürün Kodu', accessor: 'productCode' },
      { Header: 'Adet', accessor: 'count' },
      { Header: 'Fiyat', accessor: 'price' },
      { Header: 'Sipariş Durumu', accessor: 'orderStatus' },
      { Header: 'Sipariş No', accessor: 'sapOrderNumber' },
      { Header: 'Onay Durumu', accessor: 'approvalStatus' },
      { Header: 'Seç', accessor: 'select' },
    ],
    [],
  )

  const tableData = useMemo(
    () =>
      list.map((item, index) => {
        const ordered = hasOrderNo(item)
        const orderNoClickable = ordered && !isCancelledStatus(item)

        // ✅ Seçilebilirlik (iptal): sipariş no OLMAMALI ve iptal olmamış olmalı
        const selectableForCancel = !ordered && !isCancelledStatus(item)

        const approval = getDealerApproval(item)

        return {
          rowNo: index + 1,
          productName: item.ProductName,
          productNameFromDb: item.ProductNameFromDB,
          productCode: item.ProductCode,
          count: <span>{item.Count}</span>,
          price: (
            <span style={{ whiteSpace: 'nowrap' }}>
              {formatMoney2(item.PriceFromB2b) ?? '-'} {currencySymbolFromRow(item)}
            </span>
          ),
          orderStatus:
            item.StatusText?.length > 0 && item.Status !== -1
              ? item.StatusText
              : getStatusLabel(item.Status),
          sapOrderNumber: (
            <span
              role={orderNoClickable ? 'button' : undefined}
              onClick={() => {
                if (!orderNoClickable) return
                openOrderNoModal(item.SaporderNumber)
              }}
              title={
                orderNoClickable
                  ? 'Sipariş detayını görüntüle'
                  : item.Status === -1
                    ? 'İptal siparişler tıklanamaz'
                    : 'Sipariş numarası yok'
              }
              style={{
                cursor: orderNoClickable ? 'pointer' : 'not-allowed',
                color: orderNoClickable ? '#0d6efd' : '#6c757d',
                textDecoration: orderNoClickable ? 'underline' : 'none',
                fontWeight: 600,
                display: 'inline-block',
              }}
            >
              {item.SaporderNumber || '-'}
            </span>
          ),
          approvalStatus: (
            <span
              className={
                approval === 2
                  ? 'badge badge-success'
                  : approval === -1
                    ? 'badge badge-danger'
                    : approval === 1
                      ? 'badge badge-info'
                      : approval === 0
                        ? 'badge badge-warning'
                        : 'badge badge-secondary'
              }
            >
              {getDealerApprovalLabel(item)}
            </span>
          ),
          select: (
            <input
              type='checkbox'
              checked={selectedIds.includes(item.Id)}
              onChange={() => toggleSelect(item.Id)}
              disabled={!selectableForCancel && !isDealerApprovalPendingRow(item)}
              title={
                isDealerApprovalPendingRow(item)
                  ? 'Onayla/Reddet için seç'
                  : selectableForCancel
                    ? 'İptal etmek için seç'
                    : ordered
                      ? 'Sipariş numarası oluşmuş ürün iptal edilemez'
                      : 'İptal edilmiş ürün tekrar iptal edilemez'
              }
            />
          ),
        }
      }),
    [list, selectedIds],
  )

  const refNo = headerInfo.ReferenceNumber || list[0]?.ReferenceNumber

  // ===== Sipariş No Modal (Detay) =====
  const modalRow: PreOrderListModel | null = useMemo(() => {
    return selectedOrderRows.length > 0 ? selectedOrderRows[0] : null
  }, [selectedOrderRows])

  const modalOrderHeader = useMemo(() => {
    const od = (modalRow?.OrderDetail?.[0] as IGetMyOrdersResponse | undefined) || undefined
    const lines = (modalRow?.OrderDetail2 || []) as IGetOrderDetailsResponse[]

    const currencyCode =
      (od?.DOVIZ_KODU || lines?.[0]?.SIPARIS_PARA_BIRIMI || '').toString()
    const currencySymbol = currencySymbolFromCode(currencyCode)

    const statusText =
      (modalRow?.StatusText && modalRow.StatusText.trim().length > 0
        ? modalRow.StatusText
        : getStatusLabel(modalRow?.Status)) || ''

    const orderDate = stripTimeFromSapDate(od?.SIPARIS_TARIHI)
    const paymentText = (od?.ODEME_BICIMI || '').trim()

    const shipmentInfoSet = new Set<string>()
    if (lines && lines.length > 0) {
      for (const l of lines) {
        const label = (l.SEVK_ACIKLAMA || '').trim()
        const date = stripTimeFromSapDate((l as any).SEVK_TARIHI)
        const txt = [label, date].filter(Boolean).join(' - ').trim()
        if (txt) shipmentInfoSet.add(txt)
      }
    }
    if (shipmentInfoSet.size === 0) {
      const firstSevk = (od?.SEVK_ACIKLAMA || '').trim()
      if (firstSevk) shipmentInfoSet.add(firstSevk)
    }
    const shipmentInfo = Array.from(shipmentInfoSet).join(' | ')

    return { statusText, orderDate, paymentText, currencySymbol, shipmentInfo }
  }, [modalRow])

  const orderLinesForModal = useMemo(() => {
    const lines = (modalRow?.OrderDetail2 || []) as IGetOrderDetailsResponse[]
    return lines.map(l => {
      const sym = currencySymbolFromCode(l.SIPARIS_PARA_BIRIMI)

      const lineKdv =
        typeof (l as any).SPB_TOPLAM_KDV === 'number'
          ? (l as any).SPB_TOPLAM_KDV
          : typeof (l as any).SPB_KDV === 'number'
            ? (l as any).SPB_KDV
            : typeof (l as any).KDV_TUTARI === 'number'
              ? (l as any).KDV_TUTARI
              : null

      return {
        URUN_KODU: l.URUN_KODU,
        URUN_ACIKLAMA: l.URUN_ACIKLAMA,
        TOPLAM_ADET: l.TOPLAM_ADET,
        BIRIM_TUTAR: l.BIRIM_TUTAR,
        TOPLAM_TUTAR: l.TOPLAM_TUTAR,
        PARA: sym,
        KDV: lineKdv,
      }
    })
  }, [modalRow])

  const modalTotals = useMemo(() => {
    const sym = modalOrderHeader.currencySymbol || ''
    const totalNet = orderLinesForModal.reduce((acc, l) => acc + (Number(l.TOPLAM_TUTAR) || 0), 0)
    const totalKdv = orderLinesForModal.reduce((acc, l) => acc + (Number(l.KDV) || 0), 0)
    const grandTotal = totalNet + totalKdv
    return { totalNet, totalKdv, grandTotal, sym }
  }, [orderLinesForModal, modalOrderHeader.currencySymbol])

  // ===== Top computed info (bilgilendirme amaçlı) =====
  const selectedRows = useMemo(() => list.filter(x => selectedIds.includes(x.Id)), [list, selectedIds])

  // Cancel butonu: seçililer içinde sipariş no OLMAYAN + iptal olmayan varsa işlem yapılabilir (buton disable değil, içeride uyarı var)
  const hasCancelableSelected = useMemo(
    () => selectedRows.some(x => !hasOrderNo(x) && !isCancelledStatus(x)),
    [selectedRows],
  )

  const hasPendingSelected = useMemo(
    () => selectedRows.some(x => isDealerApprovalPendingRow(x)),
    [selectedRows],
  )

  return (
    <div>
      <div className='content col-lg-12 m-auto'>
        <div className=''>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <span className='h4'>
              Ön Sipariş Görüntüle | <span style={{ color: 'red' }}>{refNo}</span>
            </span>

            {/* ✅ Üst aksiyonlar: Onayla / Reddet (disable olmayacak) */}
            <div className='d-flex gap-2'>
              <IPButton text='Onayla' onClick={handleApproveSelected} disabled={false} />
              <IPButton text='Reddet' onClick={handleRejectSelected} disabled={false} />
            </div>
          </div>

          <div className=''>
            {/* ÜST AKSİYON: iptal */}
            <div className='mb-4'>
              <div className='row align-items-end'>
                <div className='col-12 col-md-6 col-lg-4'>
                  <div className='d-flex gap-2'>
                    <IPButton text='Sipariş İptal Et' onClick={handleCancelSelected} disabled={false} style={{ flex: 1 }} />
                  </div>

                  <div className='mt-2' style={{ fontSize: 12, color: '#6c757d' }}>
                    İptal: Sadece sipariş numarası <b>oluşmamış</b> ve iptal edilmemiş ürünler iptal edilebilir.
                    Onay/Reddet: Sadece <b>Onay Bekliyor</b> durumundaki ürünlerde işlem yapılır.
                  </div>

                  {/* Bilgi amaçlı: seçime göre durum */}
                  {selectedIds.length > 0 && (
                    <div className='mt-1' style={{ fontSize: 12, color: '#6c757d' }}>
                      Seçim: {hasCancelableSelected ? 'İptal edilebilir ürün var' : 'İptal edilebilir ürün yok'} |{' '}
                      {hasPendingSelected ? 'Onay bekleyen ürün var' : 'Onay bekleyen ürün yok'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ADRES / FATURA - READONLY */}
            <div className='card mb-4'>
              <div className='card-header'>Adres / Fatura Bilgileri</div>
              <div className='card-body'>
                <div className='row'>
                  <div className='col-lg-3 mb-3'>
                    <label>Firma Adı</label>
                    <IPInput type='text' value={headerInfo.CompanyName || ''} disabled={true} />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Bayi Kodu</label>
                    <IPInput
                      type='text'
                      value={headerInfo.DealerCode?.replace('0000', '') || ''}
                      disabled={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Bayi Adı</label>
                    <IPInput
                      type='text'
                      value={headerInfo.DealerName ?? ''}
                      disabled={true}
                      style={{ cursor: 'not-allowed' }}
                    />
                  </div>
                  <div className='col-lg-4 mb-3'>
                    <label>Mail</label>
                    <IPInput type='text' value={headerInfo.Mail || ''} disabled={true} />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-lg-2 mb-3'>
                    <label>Vergi Dairesi</label>
                    <IPInput type='text' value={headerInfo.TaxOffice || ''} disabled={true} />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Vergi No</label>
                    <IPInput type='text' value={headerInfo.TaxNumber || ''} disabled={true} />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>Telefon</label>
                    <IPInput type='text' value={headerInfo.PhoneNumber || ''} disabled={true} />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Teslim Alan Adı</label>
                    <IPInput type='text' value={headerInfo.TakerName || ''} disabled={true} />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Teslim Alan Soyadı</label>
                    <IPInput type='text' value={headerInfo.TakerSurname || ''} disabled={true} />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-lg-2 mb-3'>
                    <label>İl</label>
                    <IPInput type='text' value={headerInfo.City || ''} disabled={true} />
                  </div>
                  <div className='col-lg-2 mb-3'>
                    <label>İlçe</label>
                    <IPInput type='text' value={headerInfo.District || ''} disabled={true} />
                  </div>
                  <div className='col-lg-8 mb-3'>
                    <label>Adres</label>
                    <IPInput type='text' value={headerInfo.Address || ''} disabled={true} />
                  </div>
                </div>

                <br />

                <div className='row'>
                  <div className='col-lg-3 mb-3'>
                    <label>Sipariş Tipi</label>
                    <IPInput type='text' value={headerInfo.Type || ''} disabled={true} />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Firma Tipi</label>
                    <IPInput type='text' value={headerInfo.CompanyType || ''} disabled={true} />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Satış Yöneticisi</label>
                    <IPInput type='text' value={headerInfo.Manager || ''} disabled={true} />
                  </div>
                  <div className='col-lg-3 mb-3'>
                    <label>Satış Yöneticisi Mail</label>
                    <IPInput type='text' value={headerInfo.ManagerMail || ''} disabled={true} />
                  </div>
                </div>
              </div>
            </div>

            {/* TABLE */}
            <div className='row'>
              <div className='col-12'>
                <Table columns={tableColumns} data={tableData} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sipariş No Detay Modal */}
      <Modal
        show={showOrderNoModal}
        onClose={() => setShowOrderNoModal(false)}
        hideConfirm={true}
        maxWidth={1300}
      >
        <div className='modal-content' style={{ padding: 16 }}>
          <h4 style={{ margin: 0 }}>
            Sipariş No: <span style={{ color: '#0d6efd' }}>{selectedOrderNo}</span>
          </h4>

          <hr />

          <div className='row' style={{ marginBottom: 12 }}>
            <div className='col-md-3 mb-2'>
              <b>Durum:</b> {modalOrderHeader.statusText || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Sipariş Tarihi:</b> {modalOrderHeader.orderDate || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Ödeme:</b> {modalOrderHeader.paymentText || '-'}
            </div>
            <div className='col-md-3 mb-2'>
              <b>Sevk:</b> {modalOrderHeader.shipmentInfo || '-'}
            </div>
          </div>

          <div className='table-responsive'>
            <table className='table table-sm table-hover'>
              <thead>
                <tr>
                  <th>Ürün Kodu</th>
                  <th>Açıklama</th>
                  <th style={{ textAlign: 'right' }}>Adet</th>
                  <th style={{ textAlign: 'right' }}>Birim</th>
                  <th style={{ textAlign: 'right' }}>Toplam (Net)</th>
                  <th style={{ textAlign: 'right' }}>KDV</th>
                </tr>
              </thead>
              <tbody>
                {orderLinesForModal.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ color: '#6c757d' }}>
                      Detay bulunamadı.
                    </td>
                  </tr>
                ) : (
                  orderLinesForModal.map((l, idx) => {
                    const unit = formatMoney2(l.BIRIM_TUTAR)
                    const net = formatMoney2(l.TOPLAM_TUTAR)
                    const kdv = l.KDV != null ? formatMoney2(l.KDV) : null
                    return (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{l.URUN_KODU}</td>
                        <td>{l.URUN_ACIKLAMA}</td>
                        <td style={{ textAlign: 'right' }}>{l.TOPLAM_ADET}</td>
                        <td style={{ textAlign: 'right' }}>
                          {unit != null ? `${unit} ${l.PARA}` : `-`}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {net != null ? `${net} ${l.PARA}` : `-`}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {kdv != null ? `${kdv} ${l.PARA}` : '-'}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>

              {orderLinesForModal.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Toplam (Net):</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.totalNet.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Genel KDV:</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.totalKdv.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={4}></td>
                    <td style={{ textAlign: 'right' }}>
                      <b>Genel Toplam:</b>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <b>
                        {modalTotals.grandTotal.toFixed(2)} {modalTotals.sym}
                      </b>
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </Modal>

      {/* Mevcut Sipariş Sonucu Modal (korundu) */}
      <Modal
        show={state.showOrderDetails}
        onClose={() => dispatchState({ showOrderDetails: false })}
        hideConfirm={true}
        maxWidth={1200}
      >
        <div id='order' className='modal-content'>
          <div className='form-row' style={{ justifyContent: 'center', position: 'relative' }}>
            <div className='form-group'>
              <i
                className='icon-check'
                style={{ color: 'green', fontSize: '50px', marginLeft: '160px' }}
              ></i>
              <h4>Siparişiniz Başarıyla Kaydolmuştur!</h4>
            </div>
          </div>

          {state.errorMessage &&
            state.errorMessage.length > 0 &&
            state.errorMessage.map((error, index) => (
              <div key={index}>
                <h3 style={{ color: 'red' }}>{error}</h3> <br />{' '}
              </div>
            ))}

          <p
            style={{
              marginTop: '10px',
              fontSize: '17px',
              color: 'black',
              fontWeight: '600',
              marginLeft: '20px',
            }}
          >
            SİPARİŞ SEVK ADRES BİLGİLERİ
          </p>

          <div style={{ backgroundColor: 'white', padding: '20px' }}>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Firma Adı</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.DealerName}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Sipariş No</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.OrderNumber}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>İl/İlçe</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.City + '/' + state.orderDetails.District}
            </div>
            <div className='d-flex align-items-center justify-content-start'>
              <b style={{ width: '100px' }}>Adres</b>
              <span className='mr-2'>:</span>
              {state.orderDetails.Address}
            </div>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default componentWithContext(EditPreOrder)
